

// @ts-nocheck
'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { SocialPost, SocialStory, StoryGroup } from '@/lib/types';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { X, ChevronLeft, ChevronRight, VolumeX, Volume2, Link as LinkIcon, Play, Pause } from 'lucide-react';
import { markStoryAsViewed } from '@/lib/actions';
import { useAuth } from '@/hooks/useAuth';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import { Slider } from '../ui/slider';
import Image from '@/components/ui/Image';
import ReactMarkdown from 'react-markdown';
import { cn } from '@/lib/utils';
import { Music } from 'lucide-react';

function StorySpotifyPlayer({ story, isPlaying }: { story: SocialStory, isPlaying: boolean }) {
    const [audioUrl, setAudioUrl] = useState<string | null>(null);
    const audioRef = useRef<HTMLAudioElement>(null);

    useEffect(() => {
        // Canción elegida con el buscador → preview exacta.
        if (story.audioPreviewUrl) { setAudioUrl(story.audioPreviewUrl); return; }
        // Fallback: buscar por título en iTunes (fuente fiable hoy).
        let cancelled = false;
        if (story.spotifyTrackName) {
            (async () => {
                try {
                    const r = await fetch(`https://itunes.apple.com/search?term=${encodeURIComponent(story.spotifyTrackName!)}&media=music&entity=song&limit=1`);
                    const data = await r.json();
                    const preview = data?.results?.[0]?.previewUrl;
                    if (preview && !cancelled) setAudioUrl(preview);
                } catch { /* ignore */ }
            })();
        }
        return () => { cancelled = true; };
    }, [story.spotifyUrl, story.audioPreviewUrl, story.spotifyTrackName]);

    useEffect(() => {
        if (audioRef.current && audioUrl) {
            if (isPlaying) {
                audioRef.current.play().catch(e => console.log("Story autoplay blocked", e));
            } else {
                audioRef.current.pause();
            }
        }
    }, [isPlaying, audioUrl]);

    const handleTimeUpdate = () => {
        const audio = audioRef.current;
        if (audio && story.spotifyEndTime && (audio.currentTime >= story.spotifyEndTime)) {
            audio.pause();
            if (story.spotifyStartTime) audio.currentTime = story.spotifyStartTime;
            if (isPlaying) audio.play();
        }
    };

    if (!audioUrl) return null;

    return (
        <audio 
            ref={audioRef} 
            src={audioUrl} 
            onTimeUpdate={handleTimeUpdate}
            onLoadedMetadata={() => {
                if (audioRef.current && story.spotifyStartTime) {
                    audioRef.current.currentTime = story.spotifyStartTime;
                }
            }}
            preload="auto"
            className="hidden"
        />
    );
}

const STORY_IMAGE_DURATION = 5000; // 5 seconds for images

interface StoryViewerProps {
  storyGroups: StoryGroup[];
  initialGroupIndex: number;
  onClose: () => void;
  onGroupChange: (newIndex: number) => void;
  onViewPost: (post: SocialPost) => void;
  allPosts: SocialPost[];
}

function SharedPostCard({ post, onClick }: { post: SocialPost, onClick: () => void }) {
    const mediaUrl = Array.isArray(post.mediaUrl) ? post.mediaUrl[0] : post.mediaUrl;
    const isVideo = post.mediaType === 'video' && mediaUrl;
    const isCollaboration = post.collaborationStatus === 'accepted' && post.collaboratorId;

    return (
        <button 
            onClick={onClick}
            className="w-full max-w-sm mx-auto bg-background/80 backdrop-blur-sm rounded-xl border border-white/20 shadow-lg p-3 text-left transition-transform hover:scale-105"
        >
            <div className="flex items-center gap-2 mb-2">
                <div className="flex -space-x-3">
                   <Avatar className="h-6 w-6 border-2 border-background">
                        <AvatarImage src={post.authorShieldUrl} />
                        <AvatarFallback>{post.authorName.substring(0,2)}</AvatarFallback>
                    </Avatar>
                     {isCollaboration && (
                       <Avatar className="h-6 w-6 border-2 border-background">
                            <AvatarImage src={post.collaboratorShieldUrl} />
                            <AvatarFallback>{post.collaboratorName?.substring(0,2)}</AvatarFallback>
                        </Avatar>
                    )}
                </div>
                 <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-sm font-semibold text-white truncate">{post.authorName}</span>
                    {isCollaboration && (
                        <>
                            <LinkIcon className="h-3 w-3 text-white/80"/>
                            <span className="text-sm font-semibold text-white truncate">{post.collaboratorName}</span>
                        </>
                    )}
                 </div>
            </div>
            <div className="relative aspect-square w-full rounded-md overflow-hidden bg-muted/50">
                {isVideo ? (
                     <video
                        src={mediaUrl}
                        className="w-full h-full object-cover"
                        autoPlay
                        loop
                        muted
                        playsInline
                    />
                ) : mediaUrl ? (
                    <Image src={mediaUrl} alt={post.title} className="object-cover w-full h-full" />
                ) : (
                    <div className="w-full h-full flex items-center justify-center p-4 bg-gradient-to-br from-secondary to-secondary/50">
                        <p className="text-foreground text-center line-clamp-6">{post.content}</p>
                    </div>
                )}
            </div>
        </button>
    );
}

export function StoryViewer({ storyGroups, initialGroupIndex, onClose, onGroupChange, onViewPost, allPosts }: StoryViewerProps) {
  const [currentGroupIndex, setCurrentGroupIndex] = useState(initialGroupIndex);
  const [currentStoryIndex, setCurrentStoryIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isMuted, setIsMuted] = useState(true);
  const { user } = useAuth();
  const timerRef = useRef<any>(null); 
  const startTimeRef = useRef<number>(Date.now());
  const pausedTimeRef = useRef<number | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const [volume, setVolume] = useState(0);


  const activeGroup = storyGroups[currentGroupIndex];
  const activeStory = activeGroup?.stories[currentStoryIndex];
  
  const advance = useCallback((fromVideoEnd = false) => {
    // Next story in the same group
    if (activeGroup && currentStoryIndex < activeGroup.stories.length - 1) {
      setCurrentStoryIndex(prev => prev + 1);
    } 
    // Next group
    else if (currentGroupIndex < storyGroups.length - 1) {
      setCurrentGroupIndex(prev => prev + 1);
      setCurrentStoryIndex(0);
    } 
    // Last story of last group
    else {
      onClose();
    }
  }, [currentStoryIndex, currentGroupIndex, storyGroups.length, activeGroup, onClose]);

  const goToNextStory = useCallback(() => {
    if(timerRef.current) cancelAnimationFrame(timerRef.current);
    advance();
  }, [advance]);

  const goToPrevStory = useCallback(() => {
    if(timerRef.current) cancelAnimationFrame(timerRef.current);
    if (currentStoryIndex > 0) {
      setCurrentStoryIndex(prev => prev - 1);
    } else if (currentGroupIndex > 0) {
      const prevIndex = currentGroupIndex - 1;
      const prevGroup = storyGroups[prevIndex];
      setCurrentGroupIndex(prevIndex);
      setCurrentStoryIndex(prevGroup.stories.length - 1);
    }
  }, [currentStoryIndex, currentGroupIndex, storyGroups]);

  useEffect(() => {
    onGroupChange(currentGroupIndex);
  }, [currentGroupIndex, onGroupChange]);

  useEffect(() => {
    if (!activeStory) return;

    if (user && activeStory.viewedBy && !activeStory.viewedBy.includes(user.uid)) {
      markStoryAsViewed(activeStory.id, user.uid);
      activeStory.viewedBy.push(user.uid);
    }

    setProgress(0);
    startTimeRef.current = Date.now();
    pausedTimeRef.current = null;
    if (timerRef.current) cancelAnimationFrame(timerRef.current);
    setIsPlaying(true);
    
    const musicDuration = (activeStory.spotifyUrl || activeStory.audioPreviewUrl)
      ? ((activeStory.spotifyEndTime || 15) - (activeStory.spotifyStartTime || 0)) * 1000
      : STORY_IMAGE_DURATION;
      
    if (activeStory.mediaType === 'image' || activeStory.linkedPostId) {
      const runTimer = () => {
        if (pausedTimeRef.current) return;
        const elapsed = Date.now() - startTimeRef.current;
        const newProgress = (elapsed / musicDuration) * 100;

        if (newProgress >= 100) {
          advance();
        } else {
          setProgress(newProgress);
          timerRef.current = requestAnimationFrame(runTimer);
        }
      };
      timerRef.current = requestAnimationFrame(runTimer);
    } else if (activeStory.mediaType === 'video' && videoRef.current) {
        videoRef.current.currentTime = 0;
        videoRef.current.volume = volume;
        videoRef.current.muted = isMuted;
        videoRef.current.play().catch(e => console.error("Video play failed", e));
    }

    return () => {
      if (timerRef.current) cancelAnimationFrame(timerRef.current);
    };
  }, [activeStory, advance, user, volume, isMuted]);

  const handlePause = (e: React.MouseEvent) => {
    e.preventDefault();
    if(pausedTimeRef.current === null) {
        if(timerRef.current) cancelAnimationFrame(timerRef.current);
        pausedTimeRef.current = Date.now();
        if(videoRef.current) videoRef.current.pause();
        setIsPlaying(false);
    }
  }

  const handleResume = (e: React.MouseEvent) => {
    e.preventDefault();
    if (pausedTimeRef.current && startTimeRef.current) {
      const pauseDuration = Date.now() - pausedTimeRef.current;
      startTimeRef.current += pauseDuration;
      pausedTimeRef.current = null;
      if (activeStory?.mediaType === 'image' || activeStory.linkedPostId) {
         timerRef.current = requestAnimationFrame(() => {
            const runTimer = () => {
              if (pausedTimeRef.current) return;
              const elapsed = Date.now() - startTimeRef.current;
              const musicDuration = (activeStory?.spotifyUrl || activeStory?.audioPreviewUrl)
                ? ((activeStory.spotifyEndTime || 15) - (activeStory.spotifyStartTime || 0)) * 1000
                : STORY_IMAGE_DURATION;
              const newProgress = (elapsed / musicDuration) * 100;
              if (newProgress >= 100) advance();
              else {
                setProgress(newProgress);
                timerRef.current = requestAnimationFrame(runTimer);
              }
            };
            runTimer();
        });
      } else if (videoRef.current) {
        videoRef.current.play().catch(e => console.error("Video resume failed", e));
      }
      setIsPlaying(true);
    }
  }
  
  const handleVideoTimeUpdate = () => {
      if (videoRef.current && videoRef.current.duration) {
          const newProgress = (videoRef.current.currentTime / videoRef.current.duration) * 100;
          setProgress(newProgress);
      }
  }

  
  if (!activeGroup || !activeStory) {
    return null;
  }
  
  const getStoryTimestamp = (dateInput: any) => {
    try {
      let date: Date;
      if (dateInput && typeof dateInput.toDate === 'function') {
        date = dateInput.toDate();
      } else {
        date = new Date(dateInput);
      }
      if (isNaN(date.getTime())) return '';
      const distance = formatDistanceToNow(date, { locale: es, addSuffix: false });
      return distance
          .replace('about ', '').replace('almost ', '')
          .replace('less than a minute', '1m').replace(' minute', 'm').replace(' minutes', 'm')
          .replace(' hour', 'h').replace(' hours', 'h')
          .replace(' day', 'd').replace(' days', 'd');
    } catch (e) {
        return '';
    }
  }

  const toggleMute = (e: React.MouseEvent) => {
      e.stopPropagation();
      const newMutedState = !isMuted;
      setIsMuted(newMutedState);
      if (videoRef.current) {
        videoRef.current.muted = newMutedState;
        setVolume(newMutedState ? 0 : 0.5); // Reset volume when unmuting
      }
  }
  
  const handleVolumeChange = (value: number[]) => {
      const newVolume = value[0];
      setVolume(newVolume);
      setIsMuted(newVolume === 0);
      if (videoRef.current) {
          videoRef.current.volume = newVolume;
          videoRef.current.muted = newVolume === 0;
      }
  };

  const linkedPost = activeStory.linkedPostId ? allPosts.find(p => p.id === activeStory.linkedPostId) : undefined;
  
  const renderMedia = () => {
    const url = activeStory.mediaUrl as string;
    
    if (activeStory.mediaType === 'video') {
        return (
          <video
            ref={videoRef}
            src={url}
            className="w-full h-full object-contain"
            autoPlay
            playsInline
            muted={isMuted}
            onEnded={() => advance(true)}
            onTimeUpdate={handleVideoTimeUpdate}
            key={activeStory.id}
          />
        );
    }
    
    return (
      <Image 
        src={url}
        alt={`Historia de ${activeGroup.name}`} 
        className="object-contain w-full h-full"
      />
    );
  };


  return (
    <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center animate-in fade-in-0" onContextMenu={(e) => e.preventDefault()}>
      <div className="relative w-full max-w-[400px] aspect-[9/16] bg-black rounded-lg overflow-hidden shadow-2xl">
        <div className="absolute top-0 left-0 right-0 p-4 z-10 bg-gradient-to-b from-black/50 to-transparent">
          <div className="flex items-center gap-2">
            {activeGroup.stories.map((_, index) => (
              <div key={index} className="flex-1 h-0.5 bg-white/30 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-white rounded-full transition-all duration-100 ease-linear" 
                  style={{ width: `${index < currentStoryIndex ? 100 : (index === currentStoryIndex ? progress : 0)}%` }}
                />
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between mt-3">
             <div className="flex items-center gap-3">
                <Avatar className="h-9 w-9 border-2 border-white/80">
                    <AvatarImage src={activeGroup.shield} />
                    <AvatarFallback>{activeGroup.name.substring(0,2)}</AvatarFallback>
                </Avatar>
                <div className='flex flex-col'>
                    <div className='flex items-center gap-2'>
                        <span className="text-white font-semibold text-sm">{activeGroup.name}</span>
                        <span className="text-white/70 text-xs">{getStoryTimestamp(activeStory.createdAt)}</span>
                    </div>
                    {(activeStory.spotifyUrl || activeStory.audioPreviewUrl || activeStory.spotifyTrackName) && (
                        <div className="flex w-full items-center gap-1.5 text-[10px] text-white/90 font-bold overflow-hidden mt-0.5">
                            <Music className="h-2.5 w-2.5" />
                            <span className="truncate">{activeStory.spotifyTrackName || "Audio"}</span>
                        </div>
                    )}
                </div>
             </div>
              <div className='flex items-center gap-2 group/volume'>
                {activeStory.mediaType === 'video' && !linkedPost && (
                    <div className="flex items-center gap-2">
                        <button onClick={toggleMute} className="text-white/80 hover:text-white transition-colors">
                            {isMuted || volume === 0 ? <VolumeX size={20} /> : <Volume2 size={20} />}
                        </button>
                        <div className="w-0 group-hover/volume:w-20 transition-[width] duration-300">
                           <Slider
                             value={[volume]}
                             onValueChange={handleVolumeChange}
                             max={1}
                             step={0.1}
                           />
                        </div>
                    </div>
                )}
                <button onClick={onClose} className="text-white/80 hover:text-white transition-colors">
                    <X size={24} />
                </button>
             </div>
          </div>
        </div>
        {(activeStory.spotifyUrl || activeStory.audioPreviewUrl || activeStory.spotifyTrackName) && <StorySpotifyPlayer story={activeStory} isPlaying={isPlaying} />}
        
        {linkedPost ? (
            <div className="w-full h-full relative flex items-center justify-center p-8">
                 <Image 
                    src={Array.isArray(linkedPost.mediaUrl) ? linkedPost.mediaUrl[0] : linkedPost.mediaUrl || linkedPost.authorShieldUrl}
                    alt={`Fondo para ${linkedPost.title}`} 
                    className="object-cover blur-lg scale-110 w-full h-full absolute inset-0"
                  />
                  <div className="absolute inset-0 bg-gradient-to-b from-primary/20 via-primary/50 to-primary/80 opacity-50"></div>
                  <div className="relative z-10 w-full flex items-center justify-center">
                     <SharedPostCard post={linkedPost} onClick={() => onViewPost(linkedPost)} />
                  </div>
            </div>
        ) : (
          renderMedia()
        )}

        {activeStory.text && !linkedPost && (
            <div className="absolute bottom-0 left-0 right-0 p-8 pb-12 z-10 bg-gradient-to-t from-black/60 to-transparent">
                <p className="text-white text-center text-lg font-semibold drop-shadow-lg whitespace-pre-wrap">{activeStory.text}</p>
            </div>
        )}
        
        <div className="absolute inset-0 flex">
            <div className="flex-1" onClick={goToPrevStory} onMouseDown={handlePause} onMouseUp={handleResume} onMouseLeave={handleResume} />
            <div className="flex-1" onClick={goToNextStory} onMouseDown={handlePause} onMouseUp={handleResume} onMouseLeave={handleResume} />
        </div>
        
         <button onClick={goToPrevStory} className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-black rounded-full h-8 w-8 flex items-center justify-center">
            <ChevronLeft size={20} />
         </button>
         <button onClick={goToNextStory} className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-black rounded-full h-8 w-8 flex items-center justify-center">
            <ChevronRight size={20} />
         </button>
      </div>
    </div>
  );
}
